import '@popperjs/core';
import * as basicLightbox from 'basiclightbox';
import * as bootstrap from 'bootstrap';
import { tns } from 'tiny-slider';
import Toastify from 'toastify-js';
import 'zangdar';

//
// Utils
//

// Convert string boolean
const checkBool = (val) => {
  if (val === 'true') {
    return true;
  }

  if (val === 'false') {
    return false;
  }

  return val;
};

const supportsVideoType = (type) => {
  const formats = {
    ogg: 'video/ogg; codecs="theora"',
    h264: 'video/mp4; codecs="avc1.42E01E"',
    webm: 'video/webm; codecs="vp8, vorbis"',
    vp9: 'video/webm; codecs="vp9"',
    hls: 'application/x-mpegURL; codecs="avc1.42E01E"',
  };

  const video = document.createElement('video');

  return video.canPlayType(formats[type] || type);
};

const apiRequest = (url = '', data = {}, callback, method = 'POST') => {
  fetch(url, {
    method,
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
    },
    redirect: 'follow',
    referrerPolicy: 'no-referrer',
    body: JSON.stringify(data),
  })
    .then(({ ok }) => {
      callback(ok);
    })
    .catch(() => {
      callback(false);
    });
};
//
// Init
//

document.addEventListener('DOMContentLoaded', () => {
  initNavbar();
  initTooltips();
  initTabScrolls();
  initSliders();
  initOverlays();
  initNavCarousels();
  initLazyElements();
  initPricingTables();
  initSignUp();
  initContact();
  initPricingDeal();

  window.addEventListener('scroll', () => {
    initNavbar();
  });
});

const initNavbar = () => {
  const navbar = document.getElementById('navbar-main');
  const isDark = navbar.getAttribute('data-theme') === 'dark';

  if (window.scrollY > 0) {
    if (isDark) {
      navbar.classList.add('navbar-light');
      navbar.classList.remove('navbar-dark');
    }

    navbar.classList.add('fixed-top');
  } else {
    navbar.classList.remove('fixed-top');

    if (isDark) {
      navbar.classList.remove('navbar-light');
      navbar.classList.add('navbar-dark');
    }
  }
};

const initTooltips = () => {
  const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
  [...tooltipTriggerList].map((tooltipTriggerEl) => new bootstrap.Tooltip(tooltipTriggerEl));
};

const initTabScrolls = () => {
  if (typeof tns === 'undefined') {
    return;
  }

  const initTabScroll = (el) => {
    if (!el) {
      return;
    }

    el.addEventListener('click', (event) => {
      event.preventDefault();

      const feature = el.getAttribute('data-feature');
      const target = el.getAttribute('data-tab');
      const tab = document.querySelector(`[data-bs-target='#${target}']`);

      if (tab) {
        tab.click();

        document.getElementById(feature).scrollIntoView({
          behavior: 'smooth',
        });
      }
    });
  };

  const elements = Array.prototype.slice.call(document.querySelectorAll('[data-tab-scroll="true"]'), 0);

  if (!elements && elements.length === 0) {
    return;
  }

  elements.forEach((el) => {
    if (el.getAttribute('data-initialized') === '1') {
      return;
    }

    initTabScroll(el);

    el.setAttribute('data-initialized', '1');
  });
};

const initSliders = () => {
  if (typeof tns === 'undefined') {
    return;
  }

  const initSlider = (el) => {
    if (!el) {
      return;
    }

    const tnsOptions = {};

    // get extra options via data attributes
    el.getAttributeNames().forEach((attrName) => {
      // more options; https://github.com/ganlanyuan/tiny-slider#options
      if (/^data-tns-.*/g.test(attrName)) {
        let optionName = attrName
          .replace('data-tns-', '')
          .toLowerCase()
          .replace(/(?:[\s-])\w/g, (match) => match.replace('-', '').toUpperCase());

        if (attrName === 'data-tns-responsive') {
          // fix string with a valid json
          const jsonStr = el.getAttribute(attrName).replace(/(\w+:)|(\w+ :)/g, (matched) => `"${matched.substring(0, matched.length - 1)}":`);
          try {
            // convert json string to object
            tnsOptions[optionName] = JSON.parse(jsonStr);
          } catch (e) { }
        } else {
          tnsOptions[optionName] = checkBool(el.getAttribute(attrName));
        }
      }
    });

    const opt = Object.assign(
      {},
      {
        autoplay: true,
        autoplayHoverPause: true,
        autoplayTimeout: 3000,
        autoplayButtonOutput: false,
        container: el,
        speed: 400,
      },
      tnsOptions
    );

    if (el.closest('.tns')) {
      el.closest('.tns').classList.add('tns-initialized');
    }

    return tns(opt);
  };

  const elements = Array.prototype.slice.call(document.querySelectorAll('[data-tns="true"]'), 0);

  if (!elements && elements.length === 0) {
    return;
  }

  elements.forEach((el) => {
    if (el.getAttribute('data-initialized') === '1') {
      return;
    }

    initSlider(el);

    el.setAttribute('data-initialized', '1');
  });
};

const initOverlays = () => {
  if (typeof basicLightbox === 'undefined') {
    return;
  }

  const initOverlay = (el) => {
    if (!el) {
      return;
    }

    const overlayOptions = {};

    // get extra options via data attributes
    el.getAttributeNames().forEach((attrName) => {
      if (/^data-lightbox-.*/g.test(attrName)) {
        let optionName = attrName
          .replace('data-lightbox-', '')
          .toLowerCase()
          .replace(/(?:[\s-])\w/g, (match) => match.replace('-', '').toUpperCase());

        overlayOptions[optionName] = checkBool(el.getAttribute(attrName));
      }
    });

    const opt = Object.assign({}, overlayOptions);

    if (opt.type === 'video') {
      let src = opt.src;

      src += supportsVideoType('webm') === 'probably' ? '.webm' : '.mov';

      const instance = basicLightbox.create(`<video class="video rounded-4" src="${src}" autoplay playsinline muted loop controls></video>`, {
        closable: opt.closable === true,
        onShow(instance) {
          const video = instance.element().getElementsByClassName('video')[0];
          video.play();
        },
      });

      el.addEventListener('click', (event) => {
        event.preventDefault();

        instance.show();

        return false;
      });
    }
  };

  const elements = Array.prototype.slice.call(document.querySelectorAll('[data-lightbox="true"]'), 0);

  if (!elements && elements.length === 0) {
    return;
  }

  elements.forEach((el) => {
    if (el.getAttribute('data-initialized') === '1') {
      return;
    }

    initOverlay(el);

    el.setAttribute('data-initialized', '1');
  });
};

function initNavCarousels() {
  const initCarousel = (el) => {
    if (!el) {
      return;
    }

    let timeoutId;

    const changeTab = () => {
      const linkEl = el.querySelector('.nav-link.active');

      if (linkEl) {
        const itemEl = linkEl.parentElement;
        const itemsEl = itemEl.parentNode.children;
        const index = [].indexOf.call(itemsEl, itemEl);
        const nextEl = itemsEl[index < itemsEl.length - 1 ? index + 1 : 0];

        if (nextEl) {
          const triggerEl = nextEl.querySelector('.nav-link');
          const triggerElDelay = parseInt(triggerEl.getAttribute('data-nav-carousel-time')) * 1000;

          const tabTrigger = new bootstrap.Tab(triggerEl);

          tabTrigger.show();

          timeoutId = window.setTimeout(changeTab, triggerElDelay);
        }
      }
    };

    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // ENTER
          const linkEl = el.querySelector('.nav-link.active');

          if (linkEl) {
            const linkElDelay = parseInt(linkEl.getAttribute('data-nav-carousel-time')) * 1000;

            timeoutId = window.setTimeout(changeTab, linkElDelay);

            el.querySelectorAll('.nav-link').forEach((linkEl) => {
              linkEl.addEventListener('click', (event) => {
                event.preventDefault();

                if (timeoutId) window.clearTimeout(timeoutId);
              });
            });
          }
          return;
        }

        // LEAVE
        if (timeoutId) window.clearTimeout(timeoutId);
      },
      {
        root: null,
        threshold: 0,
      }
    );

    observer.observe(el);
  };

  const elements = Array.prototype.slice.call(document.querySelectorAll('[data-nav-carousel="true"]'), 0);

  if (!elements && elements.length === 0) {
    return;
  }

  elements.forEach((el) => {
    initCarousel(el);
  });
}

const initLazyElements = () => {
  const initLazyElement = (el) => {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const lazySrc = el.getAttribute('data-src');

          if (lazySrc) {
            let src = lazySrc;

            if (el.nodeName === 'VIDEO') {
              src += supportsVideoType('webm') === 'probably' ? '.webm' : '.mov';
            }

            el.src = src;
            el.removeAttribute('data-src');

            observer.unobserve(el);
          }

          return;
        }
      },
      {
        root: null,
        threshold: 0,
      }
    );

    observer.observe(el);
  };

  const elements = Array.prototype.slice.call(document.querySelectorAll('[data-src]'), 0);

  if (!elements && elements.length === 0) {
    return;
  }

  elements.forEach((el) => {
    initLazyElement(el);
  });
};

const initPricingTables = () => {
  const initPricingTable = (el) => {
    const formEl = el.querySelector('.form-pricing-user');
    const numberEl = formEl.querySelector('.form-pricing-user-number');
    const offerList = el.querySelectorAll('.pricing-user-offer');
    const toggleOffer = (value) => {
      const number = Number(value) || 0;

      [...offerList].map(({ style }) => {
        style.display = number && number >= 2000 ? 'block' : 'none';
      });
    };
    const isNumberInRange = (range, number) => {
      const [min, max] = range.split('-').map(Number);
      return (number >= min && (number <= max || max === 0)) || false;
    };
    const findPrice = (subscription, plan, number) => {
      const price = pricingStructure['structure'][plan];

      for (const key of Object.keys(price)) {
        if (isNumberInRange(key, number)) {
          const priceRate = price[key]['pricePerMember'][subscription][currentCurrency];

          return (number * priceRate).toFixed(2);
        }
      }

      return 0;
    };
    const calculatePrice = (value) => {
      const number = Number(value) || 0;

      toggleOffer(number);

      el.querySelector('.pricing-yearly-basic-price').innerHTML = findPrice('yearly', 'basic', number);
      el.querySelector('.pricing-yearly-standard-price').innerHTML = findPrice('yearly', 'standard', number);
      el.querySelector('.pricing-yearly-premium-price').innerHTML = findPrice('yearly', 'premium', number);

      el.querySelector('.pricing-monthly-basic-price').innerHTML = findPrice('monthly', 'basic', number);
      el.querySelector('.pricing-monthly-standard-price').innerHTML = findPrice('monthly', 'standard', number);
      el.querySelector('.pricing-monthly-premium-price').innerHTML = findPrice('monthly', 'premium', number);
    };

    formEl.addEventListener('submit', (event) => {
      event.preventDefault();

      calculatePrice(numberEl.value);

      return false;
    });

    numberEl.addEventListener('input', (event) => {
      event.preventDefault();

      calculatePrice(numberEl.value);

      return false;
    });

    const initialMembersCount = pricingStructure['initialMembersCount'];
    numberEl.value = initialMembersCount;
    calculatePrice(initialMembersCount);
    toggleOffer(initialMembersCount);
  };

  const elements = Array.prototype.slice.call(document.querySelectorAll('[data-pricing-table="true"]'), 0);

  if (!elements && elements.length === 0) {
    return;
  }

  elements.forEach((el) => {
    initPricingTable(el);
  });
};

const initSignUp = () => {
  let wizardInstance;
  const initBreadcrumbs = (wizard, breadcrumbsEl, steps) => {
    breadcrumbsEl.innerHTML = '';

    for (let item in steps) {
      if (steps.hasOwnProperty(item)) {
        const step = steps[item];
        const itemEl = document.createElement('li');
        const linkEl = document.createElement('button');

        itemEl.classList.add('nav-item');
        linkEl.classList.add('nav-link');

        if (step.active) itemEl.classList.add('active');

        let html = `<span class="me-2 shape shape-sm fs-6 text-center rounded-3">0${step.index + 1}</span>`;
        html += `<span class="fw-medium fs-6 text-uppercase">${step.label}</span>`;
        linkEl.innerHTML = html;

        linkEl.addEventListener('click', (event) => {
          event.preventDefault();
          wizard.revealStep(step);
          return false;
        });

        itemEl.appendChild(linkEl);

        breadcrumbsEl.appendChild(itemEl);
      }
    }
  };
  const initPricingTable = (el) => {
    const formEl = el.querySelector('.form');
    const numberEl = formEl.querySelector('.form-sign-up-user-number');
    const isNumberInRange = (range, number) => {
      const [min, max] = range.split('-').map(Number);
      return (number >= min && (number <= max || max === 0)) || false;
    };
    const findPrice = (subscription, plan, number) => {
      const price = pricingStructure['structure'][plan];

      for (const key of Object.keys(price)) {
        if (isNumberInRange(key, number)) {
          const priceRate = price[key]['pricePerMember'][subscription][currentCurrency];

          return (number * priceRate).toFixed(2);
        }
      }

      return 0;
    };
    const calculatePrice = (value) => {
      const number = Number(value) || 0;

      el.querySelector('.sign-up-yearly-basic-price').innerHTML = findPrice('yearly', 'basic', number);
      el.querySelector('.sign-up-yearly-standard-price').innerHTML = findPrice('yearly', 'standard', number);
      el.querySelector('.sign-up-yearly-premium-price').innerHTML = findPrice('yearly', 'premium', number);

      el.querySelector('.sign-up-monthly-basic-price').innerHTML = findPrice('monthly', 'basic', number);
      el.querySelector('.sign-up-monthly-standard-price').innerHTML = findPrice('monthly', 'standard', number);
      el.querySelector('.sign-up-monthly-premium-price').innerHTML = findPrice('monthly', 'premium', number);
    };

    calculatePrice(numberEl.value);
  };
  const initSignWizard = (el) => {
    const formEl = el.querySelector('.form');
    const stepsEl = el.querySelector('.steps');
    const wizard = new Zangdar(formEl, {
      onStepChange: () => {
        initBreadcrumbs(wizard, stepsEl, wizard.getBreadcrumb());
        initPricingTable(el);
      },
      onSubmit(event) {
        event.preventDefault();

        const loadingMessage = Toastify({
          text: el.querySelector(`.sign-up-loading-message`).innerHTML,
          className: 'primary',
          close: false,
          duration: 30000,
        }).showToast();

        const elements = event.target.elements;

        elements.save.disabled = true;

        const application = elements['application'].value;
        const email = elements['work-email'].value;
        const fullName = elements['name'].value;
        const institution = elements['institution'].value;
        const message = '.';
        const users = elements['user-number'].value;
        let subscription = '';
        let plan = '';
        let price = '';

        [...elements['yearly-plan']].map(({ checked }, index) => {
          if (checked) {
            subscription = 'yearly';

            if (index == 2) plan = 'premium';
            else if (index == 1) plan = 'standard';
            else plan = 'basic';

            price = el.querySelector(`.sign-up-yearly-${plan}-price`).innerHTML;
          }
        });

        [...elements['monthly-plan']].map(({ checked }, index) => {
          if (checked) {
            subscription = 'monthly';

            if (index == 2) plan = 'premium';
            else if (index == 1) plan = 'standard';
            else plan = 'basic';

            price = el.querySelector(`.sign-up-monthly-${plan}-price`).innerHTML;
          }
        });
        const metadata = `application: ${application}, users: ${users}, subscription: ${subscription}, plan: ${plan}, price: ${price}, form: sign-up`;

        apiRequest(
          'https://test-resources.emaq.ba/Identity/ContactUs',
          {
            email,
            fullName,
            institution,
            message,
            metadata,
          },
          (isOk) => {
            loadingMessage.hideToast();

            elements.save.disabled = false;

            if (!isOk) {
              Toastify({
                text: el.querySelector(`.sign-up-danger-message`).innerHTML,
                className: 'danger',
              }).showToast();
            } else {
              const modalInstance = bootstrap.Modal.getInstance(el);

              Toastify({
                text: el.querySelector(`.sign-up-success-message`).innerHTML,
                className: 'success',
              }).showToast();

              modalInstance.hide();

              formEl.reset();
            }
          }
        );

        return false;
      },
    });

    initBreadcrumbs(wizard, stepsEl, wizard.getBreadcrumb());

    wizardEl.addEventListener('shown.bs.modal', () => {
      const numberEl = document.querySelector('.form-pricing-user-number');

      if (numberEl && numberEl.value) {
        const formUserEl = formEl.querySelector('.form-sign-up-user-number');

        if (formUserEl) formUserEl.value = numberEl.value;
      }
    });

    const triggerEls = document.querySelectorAll('[data-bs-target="#sign-up"]');

    triggerEls.forEach((triggerEl) => {
      triggerEl.addEventListener('click', () => {
        const subscription = triggerEl.getAttribute('data-bs-subscription');
        const plan = triggerEl.getAttribute('data-bs-plan');

        if (subscription && plan) {
          const formSubscriptionEls = formEl.querySelectorAll(`[name="${subscription}-plan"]`);

          formSubscriptionEls.forEach((formSubscriptionEl) => (formSubscriptionEl.checked = false));

          formSubscriptionEls[plan === 'premium' ? 2 : plan === 'standard' ? 1 : 0].checked = true;

          document.getElementById(`sign-up-${subscription}-subscription-tab`).click();
        }
      });
    });

    wizardInstance = wizard;
  };
  const initSignForm = (formEl, wizardEl) => {
    const formEmailEl = formEl.querySelector('.form-sign-up-work-email');
    const wizardEmailEl = wizardEl.querySelector('.form-sign-up-work-email');

    formEl.addEventListener('submit', (event) => {
      event.preventDefault();

      wizardEmailEl.value = formEmailEl.value;

      if (wizardInstance) wizardInstance.revealStep(1);

      const triggerEl = document.querySelector('[data-bs-target="#sign-up"]');

      if (triggerEl) triggerEl.click();

      return false;
    });
  };

  const wizardEl = document.querySelector('[data-sign-up-wizard="true"]');

  if (!wizardEl) {
    return;
  }

  initSignWizard(wizardEl);

  const formEl = document.querySelector('[data-sign-up-form="true"]');

  if (!formEl) {
    return;
  }

  initSignForm(formEl, wizardEl);
};

const initContact = () => {
  const initContactForm = (el) => {
    const formEl = el.querySelector('.form');

    formEl.addEventListener('submit', (event) => {
      event.preventDefault();

      const loadingMessage = Toastify({
        text: el.querySelector(`.contact-loading-message`).innerHTML,
        className: 'primary',
        close: false,
        duration: 30000,
      }).showToast();

      const elements = event.target.elements;

      elements.save.disabled = true;

      const application = elements['application'].value;
      const email = elements['work-email'].value;
      const fullName = elements['name'].value;
      const institution = elements['institution'].value;
      const message = elements['message'].value;
      const metadata = `application: ${application}, form: contact-us`;

      apiRequest(
        'https://test-resources.emaq.ba/Identity/ContactUs',
        {
          email,
          fullName,
          institution,
          message,
          metadata,
        },
        (isOk) => {
          loadingMessage.hideToast();

          elements.save.disabled = false;

          if (!isOk) {
            Toastify({
              text: el.querySelector(`.contact-danger-message`).innerHTML,
              className: 'danger',
            }).showToast();
          } else {
            const modalInstance = bootstrap.Modal.getInstance(el);

            Toastify({
              text: el.querySelector(`.contact-success-message`).innerHTML,
              className: 'success',
            }).showToast();

            modalInstance.hide();

            formEl.reset();
          }
        }
      );

      return false;
    });
  };

  const el = document.querySelector('[data-contact="true"]');

  if (!el) {
    return;
  }

  initContactForm(el);
};

const initPricingDeal = () => {
  const initPricingDealForm = (el) => {
    const formEl = el.querySelector('.form');

    formEl.addEventListener('submit', (event) => {
      event.preventDefault();

      const loadingMessage = Toastify({
        text: el.querySelector(`.pricing-deal-loading-message`).innerHTML,
        className: 'primary',
        close: false,
        duration: 30000,
      }).showToast();

      const elements = event.target.elements;

      elements.save.disabled = true;

      const application = elements['application'].value;
      const email = elements['work-email'].value;
      const fullName = elements['name'].value;
      const institution = '.';
      const message = '.';
      const users = elements['users'].value;
      const price = elements['price'].value;
      const metadata = `application: ${application}, users: ${users}, price: ${price}, form: price-deal`;

      apiRequest(
        'https://test-resources.emaq.ba/Identity/ContactUs',
        {
          email,
          fullName,
          institution,
          message,
          metadata,
        },
        (isOk) => {
          loadingMessage.hideToast();

          elements.save.disabled = false;

          if (!isOk) {
            Toastify({
              text: el.querySelector(`.pricing-deal-danger-message`).innerHTML,
              className: 'danger',
            }).showToast();
          } else {
            const modalInstance = bootstrap.Modal.getInstance(el);

            Toastify({
              text: el.querySelector(`.pricing-deal-success-message`).innerHTML,
              className: 'success',
            }).showToast();

            modalInstance.hide();

            formEl.reset();
          }
        }
      );

      return false;
    });
  };

  const el = document.querySelector('[data-pricing-deal="true"]');

  if (!el) {
    return;
  }

  initPricingDealForm(el);
};
